import { Box, Button, Card, CardContent, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider.js";
import { api } from "../lib/api.js";

/**
 * Forced first-password-change gate.
 *
 * Reached on the FIRST sign-in after an invite: the user authenticated with
 * the one-time temporary password from their invite email, so the server
 * stamps `must_change` on the token and refuses every route except the auth
 * + self-service surface (plugins/auth.ts); `RequireAuth` bounces here
 * BEFORE the 2FA gate. The user sets their own password, we mint a fresh
 * token (clearing `must_change`), then continue — which lands them on the
 * mandatory 2FA enrollment next.
 */
const MIN_LENGTH = 12;

export default function SetPasswordPage(): JSX.Element {
  const { reauthenticate, logout, user } = useAuth();
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (pw.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api("/api/profile/set-initial-password", {
        method: "POST",
        body: { new_password: pw, new_password_confirmation: confirm },
      });
      // Mint a fresh token (the gate reads the `must_change` token claim, so a
      // /me-only refresh wouldn't clear it), then continue — RequireAuth now
      // routes them to mandatory 2FA enrollment.
      await reauthenticate();
      navigate("/", { replace: true });
    } catch (e) {
      setError(msg(e, "Could not set your password. Try again."));
      setBusy(false);
    }
  }

  return (
    <Box
      component="main"
      sx={{
        display: "grid",
        minHeight: "100vh",
        placeItems: "center",
        bgcolor: "background.default",
        px: 3,
        py: 5,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: "28rem", p: 3.5 }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{ fontSize: "1.25rem", fontWeight: 700, color: "text.primary" }}
          >
            Set your password
          </Typography>
          <Typography component="p" sx={{ mt: 0.75, fontSize: 14, color: "text.secondary" }}>
            You signed in with a one-time password{user?.email ? ` (${user.email})` : ""}.
            Choose your own password to continue — you'll set up two-factor
            authentication next.
          </Typography>

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            sx={{ mt: 3, display: "grid", gap: 2 }}
          >
            <TextField
              type="password"
              label="New password"
              autoComplete="new-password"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              size="small"
              fullWidth
              helperText={`At least ${MIN_LENGTH} characters.`}
            />
            <TextField
              type="password"
              label="Confirm password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              size="small"
              fullWidth
            />
            <Box>
              <Button type="submit" variant="contained" disableElevation disabled={busy}>
                {busy ? "Saving…" : "Set password & continue"}
              </Button>
            </Box>
          </Box>

          {error && (
            <Typography
              role="alert"
              sx={{
                mt: 2,
                borderRadius: 1,
                border: 1,
                borderColor: (t) => alpha(t.palette.error.main, 0.4),
                bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                px: 1.5,
                py: 1,
                fontSize: 12,
                color: "error.main",
              }}
            >
              {error}
            </Typography>
          )}

          <Box
            component="button"
            type="button"
            onClick={() => void logout()}
            sx={{
              mt: 3,
              p: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
              fontSize: 12,
              textDecoration: "underline",
              color: "text.disabled",
              "&:hover": { color: "text.primary" },
            }}
          >
            Sign out
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

function msg(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}
